// ---------------------------------------------------------------------------
// Toaster — renders toast notifications (reused from page-editor)
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react'
import { ICON_MAP } from '@/lib/icons'

const { X, Check, AlertCircle, AlertTriangle, Info } = ICON_MAP
import { useToastStore, type Toast } from '@/store/toast-store'
import { cn } from '@/lib/utils'
import { ICON, FORM } from '@/features/flow-editor/lib/design-tokens'

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setExiting(true)
    }, toast.duration - 300)
    return () => clearTimeout(exitTimer)
  }, [toast.duration])

  const Icon =
    toast.type === 'success' ? Check :
    toast.type === 'error' ? AlertCircle :
    toast.type === 'warning' ? AlertTriangle :
    Info

  return (
    <div
      className={cn(
        // RUX-08: card re-enables hit-testing inside the click-transparent
        // container below (dismiss X must stay clickable).
        'pointer-events-auto flex items-center gap-2 rounded-xl border bg-popover px-3 py-2 shadow-lg',
        'text-sm text-popover-foreground',
        'transition-all duration-300',
        exiting
          ? 'translate-y-2 opacity-0'
          : 'translate-y-0 opacity-100 animate-in slide-in-from-bottom-2 fade-in-0',
        toast.type === 'error' && 'border-destructive/30 bg-destructive/5',
        toast.type === 'success' && 'border-success/30 bg-success/5',
        toast.type === 'warning' && 'border-amber-500/30 bg-amber-500/5',
      )}
    >
      <Icon
        className={cn(
          ICON.sm,
          'shrink-0',
          toast.type === 'success' && 'text-success',
          toast.type === 'error' && 'text-destructive',
          toast.type === 'warning' && 'text-amber-500',
          toast.type === 'info' && 'text-muted-foreground',
        )}
      />
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={onRemove}
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X size={FORM.actionIconSize} />
      </button>
    </div>
  )
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  const removeToast = useToastStore((s) => s.removeToast)

  if (toasts.length === 0) return null

  return (
    <div
      // RUX-08: the fixed band must never swallow clicks aimed under it.
      className="pointer-events-none fixed bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      style={{ zIndex: 9999 }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={() => removeToast(t.id)} />
      ))}
    </div>
  )
}
